import { LookupSearchCondition, matchesLookupSearch } from './lookup-search-predicate';

// Dev-server bundles its live-reload client into this same entry; it calls location.reload(),
// which WorkerLocation doesn't implement and would otherwise crash this worker.
const workerLocation = (self as unknown as { location?: { reload?: () => void } }).location;
if (workerLocation && typeof workerLocation.reload !== 'function') {
  workerLocation.reload = () => {};
}

interface SearchRequest {
  type: 'search';
  requestId: string;
  tableName: string;
  lowerBound?: IDBValidKey;
  upperBound?: IDBValidKey;
  query: string;
  searchFields: string[];
  conditions?: LookupSearchCondition[];
  limit: number;
}

interface CancelRequest {
  type: 'cancel';
  requestId: string;
}

type WorkerMessage = SearchRequest | CancelRequest;

// Only the newest search matters; older in-flight scans stop at their next cursor step.
let currentRequestId: string | null = null;

interface SearchResponse {
  type: 'result' | 'error';
  requestId: string;
  items?: unknown[];
  metrics?: SearchMetrics;
  error?: string;
}

interface SearchMetrics {
  lowerBound?: IDBValidKey;
  upperBound?: IDBValidKey;
  databaseOpenMs: number;
  scanMs: number;
  totalMs: number;
  rowsScanned: number;
  matchedRows: number;
  cancelled: boolean;
}

const DB_NAME = 'symphony-web-lookup';
let databasePromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME);
    request.onerror = () => {
      databasePromise = null;
      reject(request.error ?? new Error('Unable to open lookup IndexedDB'));
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        databasePromise = null;
      };
      resolve(database);
    };
  });
  return databasePromise;
}

async function searchRange(request: SearchRequest): Promise<{ items: unknown[]; metrics: SearchMetrics }> {
  const startedAt = performance.now();
  const database = await openDatabase();
  const databaseOpenMs = performance.now() - startedAt;
  const scanStartedAt = performance.now();
  const keyRange =
    request.lowerBound == null
      ? request.upperBound == null
        ? undefined
        : IDBKeyRange.upperBound(request.upperBound, true)
      : request.upperBound == null
        ? IDBKeyRange.lowerBound(request.lowerBound)
        : IDBKeyRange.bound(request.lowerBound, request.upperBound, false, true);

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(request.tableName, 'readonly');
    const store = transaction.objectStore(request.tableName);
    const cursorRequest = store.openCursor(keyRange);
    const matches: unknown[] = [];
    let rowsScanned = 0;

    cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('Unable to search lookup range'));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      const wasCancelled = currentRequestId !== request.requestId;
      if (!cursor || matches.length >= request.limit || wasCancelled) {
        resolve({
          items: matches,
          metrics: {
            lowerBound: request.lowerBound,
            upperBound: request.upperBound,
            databaseOpenMs,
            scanMs: performance.now() - scanStartedAt,
            totalMs: performance.now() - startedAt,
            rowsScanned,
            matchedRows: matches.length,
            cancelled: wasCancelled,
          },
        });
        return;
      }

      rowsScanned++;
      const matchesSearch = matchesLookupSearch(cursor.value, request.query, request.searchFields, request.conditions);
      if (matchesSearch) {
        matches.push(cursor.value);
      }
      cursor.continue();
    };
  });
}

addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
  const request = event.data;
  if (!request) return;

  if (request.type === 'cancel') {
    if (currentRequestId === request.requestId) currentRequestId = null;
    return;
  }
  if (request.type !== 'search') return;

  currentRequestId = request.requestId;
  void searchRange(request)
    .then(({ items, metrics }) => {
      postMessage({ type: 'result', requestId: request.requestId, items, metrics } satisfies SearchResponse);
    })
    .catch((error: unknown) => {
      postMessage({
        type: 'error',
        requestId: request.requestId,
        error: error instanceof Error ? error.message : 'Lookup worker search failed',
      } satisfies SearchResponse);
    });
});
